/**
 * Support/Resistance Store
 *
 * Manages S/R levels for multi-timeframe analysis.
 * Receives levels from backend via WebSocket and provides them to chart components.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { SRLevel, SRLevelType, SRTimeframe, SRLevelsUpdate } from '@shared/types';
import { wsService } from '../services/WebSocketService';
import { getApiBaseUrl } from '../config/ports';
import { normalizeToBase } from '../utils/symbolUtils';

/** Visual configuration for each timeframe */
export interface SRTimeframeConfig {
  color: string;
  width: 1 | 2 | 3 | 4;  // TradingView LineWidth type
  style: 'solid' | 'dashed';
  label: string;
}

/** S/R timeframe visual configurations (ordered by importance) */
/** Colors use hex+alpha format for transparency (last 2 chars = alpha, CC = 80%, A0 = 63%) */
export const SR_TIMEFRAME_CONFIG: Record<SRTimeframe, SRTimeframeConfig> = {
  'W': {
    color: '#e91e63CC',  // Pink/Magenta - 80% opacity (most significant)
    width: 3,
    style: 'solid',
    label: 'W',
  },
  'D': {
    color: '#ffc107CC',  // Gold - 80% opacity
    width: 3,
    style: 'solid',
    label: 'D',
  },
  '4h': {
    color: '#2196f3A0',  // Blue - 63% opacity
    width: 2,
    style: 'solid',
    label: '4H',
  },
  '1h': {
    color: '#26a69aA0',  // Teal - 63% opacity
    width: 2,
    style: 'solid',
    label: '1H',
  },
  '15m': {
    color: '#9e9e9e80',  // Gray - 50% opacity
    width: 1,
    style: 'dashed',
    label: '15m',
  },
  '5m': {
    color: '#75757580',  // Dark gray - 50% opacity (most granular)
    width: 1,
    style: 'dashed',
    label: '5m',
  },
};

/** Format S/R level label for chart display - compact format */
export function formatSRLabel(level: SRLevel): string {
  const config = SR_TIMEFRAME_CONFIG[level.timeframe];
  const typeChar = level.levelType === 'support' ? 'S' : 'R';
  // Compact format: "D-R 12" or "D-R 12 3★" (confluence only if > 1)
  const confluence = level.confluenceScore > 1 ? ` ${level.confluenceScore}★` : '';
  return `${config.label}-${typeChar} ${level.touchCount}${confluence}`;
}

// ============================================================================
// Chart label formatting (presentation only — never touches how levels are
// fetched, computed or stored)
// ============================================================================

/**
 * Timeframe rank order, most significant first: W > D > 4H > 1H > 15m > 5m.
 *
 * Derived from `SR_TIMEFRAME_CONFIG`'s own key order rather than written out a
 * second time, so adding a timeframe to the config ranks it automatically and
 * there is no parallel list to drift out of sync.
 */
export const SR_TIMEFRAME_ORDER = Object.keys(SR_TIMEFRAME_CONFIG) as SRTimeframe[];

/** Rank of a timeframe; lower is more significant. Unknown timeframes rank last. */
export function srTimeframeRank(tf: SRTimeframe): number {
  const idx = SR_TIMEFRAME_ORDER.indexOf(tf);
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
}

/**
 * Distinct timeframes that actually have a level in this cluster, most
 * significant first.
 *
 * The engine never merges levels across timeframes — a weekly level and a daily
 * level at the same price stay two separate `SRLevel` objects, each keeping its
 * own `timeframe` (see `clustering.rs::ConfluenceCalculator::calculate`, which
 * only annotates them). So the timeframes backing a cluster are simply the
 * member levels' own timeframes.
 */
export function collectSRTimeframes(levels: SRLevel[]): SRTimeframe[] {
  const seen = new Set<SRTimeframe>();
  for (const level of levels) {
    seen.add(level.timeframe);
  }
  return [...seen].sort((a, b) => srTimeframeRank(a) - srTimeframeRank(b));
}

/**
 * The single highest-ranked timeframe in a cluster — the one the concise label
 * shows.
 *
 * Deliberately considers only timeframes with a level *in the cluster*, not the
 * levels' `confluentTimeframes`. A confluent timeframe may have been filtered
 * out of the view (`visibleTimeframes`), and badging the label `W` when no
 * weekly line is drawn would make the badge disagree with the chart. Confluent
 * timeframes still surface in the expansion, clearly labelled as such.
 */
export function highestSRTimeframe(levels: SRLevel[]): SRTimeframe | null {
  return collectSRTimeframes(levels)[0] ?? null;
}

/**
 * Timeframes the engine flagged as confluent with this cluster but which have
 * no drawn level in it (filtered out, or outside the render range).
 */
export function collectSRConfluentTimeframes(levels: SRLevel[]): SRTimeframe[] {
  const present = new Set(levels.map((l) => l.timeframe));
  const extra = new Set<SRTimeframe>();
  for (const level of levels) {
    for (const tf of level.confluentTimeframes ?? []) {
      if (!present.has(tf)) extra.add(tf);
    }
  }
  return [...extra].sort((a, b) => srTimeframeRank(a) - srTimeframeRank(b));
}

/**
 * Vertical gap, in CSS px, below which two levels share one label.
 *
 * This is the long-standing threshold the chart has always grouped on. It is
 * deliberately *not* the rendered label height: grouping on the label height
 * (26px) and unioning it with the store's `deltaPercent` merged far more
 * distinct price levels into a single label and visibly thinned out the chart.
 * Collapsing the label *text* was the goal; collapsing the levels themselves
 * was not. A two-row label measures ~22px, so adjacent labels can still clip
 * each other by a few px at the tightest spacing — that is the accepted trade
 * for showing every level the user expects to see.
 */
export const SR_LABEL_GROUP_THRESHOLD_PX = 18;

export interface SRLevelWithY {
  level: SRLevel;
  y: number;
}

/**
 * Collapse levels that belong to the same price cluster into one label each.
 *
 * Levels join the same cluster purely on screen distance: each level within
 * `thresholdPx` of the previous one, top to bottom, extends the current
 * cluster. Screen distance alone is what decides — a price-based tolerance
 * (the store's `deltaPercent`) was tried alongside it and merged levels the
 * chart is meant to show separately, because that tolerance is a confluence
 * annotation rule, not a rendering rule.
 */
export function groupSRLevelsByProximity(
  levelsWithY: SRLevelWithY[],
  thresholdPx: number = SR_LABEL_GROUP_THRESHOLD_PX,
): SRLevelWithY[][] {
  if (levelsWithY.length === 0) return [];

  // Sort top-to-bottom so clustering is a single linear pass.
  const sorted = [...levelsWithY].sort((a, b) => a.y - b.y);

  const groups: SRLevelWithY[][] = [];
  let current: SRLevelWithY[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = current[current.length - 1];
    const next = sorted[i];

    if (next.y - prev.y <= thresholdPx) {
      current.push(next);
    } else {
      groups.push(current);
      current = [next];
    }
  }
  groups.push(current);

  return groups;
}

/**
 * Stable identity for a rendered label cluster.
 *
 * Anchored on the cluster's highest-price member (ties broken by timeframe
 * rank) so the key survives regrouping — panning and zooming reshuffle cluster
 * membership constantly, and keying on array index would detach an open
 * expansion from its level on the very next frame.
 */
export function srGroupKey(levels: SRLevel[]): string {
  if (levels.length === 0) return '';
  let anchor = levels[0];
  for (const level of levels) {
    if (level.price > anchor.price) {
      anchor = level;
    } else if (
      level.price === anchor.price &&
      srTimeframeRank(level.timeframe) < srTimeframeRank(anchor.timeframe)
    ) {
      anchor = level;
    }
  }
  return `${anchor.price}-${anchor.timeframe}`;
}

/** The two rows a collapsed S/R label renders. */
export interface SRLabelContent {
  /** Row 1 — the cluster's price. */
  price: string;
  /** Row 2 — total touches across the cluster. */
  count: number;
  /** Row 2 — the single highest timeframe present, e.g. `W`, `D`, `4H`. */
  timeframeLabel: string;
  /** Highest timeframe's colour, matching the price line drawn for it. */
  color: string;
  /** Dominant side of the cluster. */
  levelType: SRLevelType;
  /** Mean price, kept numeric for Y-positioning. */
  priceValue: number;
}

/**
 * Build the concise two-row label for a price cluster.
 *
 * `count` is the summed `touchCount`, not `confluenceScore` and not the number
 * of levels. `confluenceScore` is not a count at all — the engine weights it
 * (bonus for daily involvement) and clamps it to 5, so it is a strength score.
 * Summed touches is also the number the label shows today, so collapsing the
 * timeframe list does not silently change what the figure means. Cluster size
 * and confluence both appear in the expansion.
 */
export function buildSRLabelContent(levels: SRLevel[], decimals = 2): SRLabelContent | null {
  if (levels.length === 0) return null;

  const highest = highestSRTimeframe(levels);
  const config = highest ? SR_TIMEFRAME_CONFIG[highest] : undefined;
  const priceValue = levels.reduce((sum, l) => sum + l.price, 0) / levels.length;
  const supports = levels.filter((l) => l.levelType === 'support').length;

  return {
    price: priceValue.toFixed(decimals),
    priceValue,
    count: levels.reduce((sum, l) => sum + l.touchCount, 0),
    timeframeLabel: config?.label ?? '',
    // Strip the alpha suffix — the label needs the solid colour, the line keeps
    // its own transparency.
    color: (config?.color ?? '#9e9e9e').slice(0, 7),
    levelType: supports >= levels.length - supports ? 'support' : 'resistance',
  };
}

/** One row of the expanded readout. */
export interface SRDetailRow {
  label: string;
  value: string;
}

function formatSRDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Everything the concise label hides — every timeframe in the cluster, the side
 * split, the exact price span, touches, strength and confluence.
 */
export function buildSRExpandedFields(levels: SRLevel[]): SRDetailRow[] {
  if (levels.length === 0) return [];

  const rows: SRDetailRow[] = [];

  const timeframes = collectSRTimeframes(levels);
  rows.push({
    label: 'Timeframes',
    value: timeframes.map((tf) => SR_TIMEFRAME_CONFIG[tf].label).join(', '),
  });

  if (levels.length > 1) {
    rows.push({ label: 'Levels', value: String(levels.length) });
  }

  const supports = levels.filter((l) => l.levelType === 'support').length;
  const resistances = levels.length - supports;
  rows.push({
    label: 'Type',
    value:
      supports > 0 && resistances > 0
        ? `${supports} support / ${resistances} resistance`
        : supports > 0
        ? 'Support'
        : 'Resistance',
  });

  const prices = levels.map((l) => l.price);
  const low = Math.min(...prices);
  const high = Math.max(...prices);
  rows.push({
    label: 'Price',
    value: low === high ? low.toFixed(2) : `${low.toFixed(2)} – ${high.toFixed(2)}`,
  });

  rows.push({
    label: 'Touches',
    value: String(levels.reduce((sum, l) => sum + l.touchCount, 0)),
  });

  rows.push({
    label: 'Strength',
    value: `${Math.max(...levels.map((l) => l.strengthScore ?? 0))}/10`,
  });

  rows.push({
    label: 'Confluence',
    value: `${Math.max(...levels.map((l) => l.confluenceScore ?? 1))}/5`,
  });

  const confluent = collectSRConfluentTimeframes(levels);
  if (confluent.length > 0) {
    rows.push({
      label: 'Confluent with',
      value: confluent.map((tf) => SR_TIMEFRAME_CONFIG[tf].label).join(', '),
    });
  }

  rows.push({
    label: 'First touch',
    value: formatSRDate(Math.min(...levels.map((l) => l.firstTouch))),
  });
  rows.push({
    label: 'Last touch',
    value: formatSRDate(Math.max(...levels.map((l) => l.lastTouch))),
  });

  return rows;
}

/**
 * Click behaviour for an S/R label: clicking the open one collapses it,
 * clicking any other opens that one instead.
 *
 * Holding a single key rather than a set is what enforces one-open-at-a-time.
 * These clusters sit within a couple of label-heights of each other, and an
 * expanded card is several times taller than a collapsed label — two open at
 * once would bury each other, which is the exact clutter this feature removes.
 */
export function toggleSRExpansion(current: string | null, key: string): string | null {
  return current === key ? null : key;
}

/** Recent touch timestamps across a cluster, newest first (max 10). */
export function collectSRTouchTimestamps(levels: SRLevel[]): number[] {
  const all = new Set<number>();
  for (const level of levels) {
    for (const ts of level.touchTimestamps ?? []) all.add(ts);
  }
  return [...all].sort((a, b) => b - a).slice(0, 10);
}

// ============================================================================
// Touch history — grouped by day, then by level
// ============================================================================

/**
 * How many days the touch history renders before it stops and says so.
 *
 * The engine keeps only the last 10 touch timestamps per level (see
 * `levels.rs::SRLevelCluster::add_touch`, which pushes then drops index 0 past
 * 10), so a cluster of six levels carries at most 60 timestamps and therefore
 * at most 60 distinct days. Live data puts the typical cluster at 3 distinct
 * days, so five covers the ordinary case whole and only bites on the long tail
 * — where the surplus is reported as "+N earlier days" rather than dropped
 * silently.
 */
export const SR_TOUCH_HISTORY_DAY_CAP = 5;

/** One level's contribution to a single day of touch history. */
export interface SRTouchLevelGroup {
  /** Level price, as displayed. */
  price: number;
  timeframe: SRTimeframe;
  /** Badge text for the timeframe, e.g. `D`, `4H` — from `SR_TIMEFRAME_CONFIG`. */
  timeframeLabel: string;
  /** Solid form of the timeframe's line colour, alpha suffix stripped. */
  color: string;
  /** Recorded touches for this level on this day. */
  count: number;
}

/** One day of touch history across a cluster. */
export interface SRTouchDayGroup {
  /** `YYYY-MM-DD` in local time — stable React key. */
  dayKey: string;
  /** Local midnight, ms. Sort key. */
  dayStart: number;
  /** e.g. `Aug 4`, or `Aug 4, 2025` when the history spans more than one year. */
  dayLabel: string;
  /** Recorded touches across every level on this day. */
  count: number;
  /** Contributing levels, most significant timeframe first. */
  levels: SRTouchLevelGroup[];
}

/** Touch history for a cluster, grouped by day then level. */
export interface SRTouchHistory {
  /** Most recent first, at most `dayCap` entries. */
  days: SRTouchDayGroup[];
  /** Days that exist but are not rendered, because of the cap. */
  hiddenDays: number;
  /** Recorded touches inside `hiddenDays`. */
  hiddenCount: number;
  /** Timestamps actually available across the cluster. */
  sampledCount: number;
  /** Summed `touchCount` — what the engine actually counted. */
  totalTouchCount: number;
  /**
   * True when the engine counted more touches than it kept timestamps for, so
   * the grouped counts are a recent sample and not the whole history.
   */
  truncated: boolean;
}

/** Local midnight for a timestamp, as ms. */
function localDayStart(ts: number): number {
  const d = new Date(ts);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * Touch history for a cluster: grouped by day, then by level within the day,
 * with a count on each. Days come back most recent first.
 *
 * Two things this deliberately does not do.
 *
 * It does not dedupe timestamps. `collectSRTouchTimestamps` unions them into a
 * Set because a flat list repeating the same instant reads as a bug; here the
 * numbers are counts, and the engine pushes one entry per touch, so two entries
 * on the same millisecond are two touches (1365 of 5123 live levels carry such
 * a pair) and collapsing them would undercount.
 *
 * It does not merge levels across timeframes. A daily and a 4-hour level at the
 * same price stay separate rows, matching how the engine stores them and how
 * "Levels in cluster" already lists them.
 *
 * Day boundary is the **local calendar day**, not the 5:02pm CT session roll.
 * The session boundary is owned by the Rust engine and the frontend has no
 * shared helper that maps an arbitrary historical timestamp to a trading day —
 * `utils/marketHours.ts` answers "is the market open now", which is a different
 * question. Writing a second 5:02pm-CT-plus-DST-plus-holidays implementation in
 * TypeScript is exactly the duplication this repo bans, so the honest choice is
 * the boundary the browser can actually compute.
 */
export function groupSRTouchHistory(
  levels: SRLevel[],
  dayCap: number = SR_TOUCH_HISTORY_DAY_CAP,
): SRTouchHistory {
  const totalTouchCount = levels.reduce((sum, l) => sum + (l.touchCount ?? 0), 0);

  // day start -> level key -> tally
  const byDay = new Map<number, Map<string, SRTouchLevelGroup>>();
  let sampledCount = 0;

  for (const level of levels) {
    const config = SR_TIMEFRAME_CONFIG[level.timeframe];
    for (const ts of level.touchTimestamps ?? []) {
      if (!Number.isFinite(ts)) continue;
      sampledCount++;

      const dayStart = localDayStart(ts);
      let dayLevels = byDay.get(dayStart);
      if (!dayLevels) {
        dayLevels = new Map();
        byDay.set(dayStart, dayLevels);
      }

      // Keyed the same way "Levels in cluster" keys its rows, so the two
      // sections agree on what counts as one level.
      const key = `${level.price}-${level.timeframe}`;
      const existing = dayLevels.get(key);
      if (existing) {
        existing.count++;
      } else {
        dayLevels.set(key, {
          price: level.price,
          timeframe: level.timeframe,
          timeframeLabel: config?.label ?? String(level.timeframe),
          color: (config?.color ?? '#9e9e9e').slice(0, 7),
          count: 1,
        });
      }
    }
  }

  const dayStarts = [...byDay.keys()].sort((a, b) => b - a);

  // Show the year only when the history actually spans more than one, so the
  // common single-year case keeps the compact "Aug 4" the card has room for.
  // Weekly levels can reach back over a year, which is where this earns itself.
  const years = new Set(dayStarts.map((d) => new Date(d).getFullYear()));
  const dayLabelOptions: Intl.DateTimeFormatOptions =
    years.size > 1
      ? { month: 'short', day: 'numeric', year: 'numeric' }
      : { month: 'short', day: 'numeric' };

  const allDays: SRTouchDayGroup[] = dayStarts.map((dayStart) => {
    const levelGroups = [...byDay.get(dayStart)!.values()].sort(
      (a, b) =>
        srTimeframeRank(a.timeframe) - srTimeframeRank(b.timeframe) || b.price - a.price,
    );
    const date = new Date(dayStart);
    return {
      dayKey: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
        date.getDate(),
      ).padStart(2, '0')}`,
      dayStart,
      dayLabel: date.toLocaleDateString('en-US', dayLabelOptions),
      count: levelGroups.reduce((sum, l) => sum + l.count, 0),
      levels: levelGroups,
    };
  });

  const cap = Math.max(0, dayCap);
  const days = allDays.slice(0, cap);
  const hidden = allDays.slice(cap);

  return {
    days,
    hiddenDays: hidden.length,
    hiddenCount: hidden.reduce((sum, d) => sum + d.count, 0),
    sampledCount,
    totalTouchCount,
    truncated: sampledCount < totalTouchCount,
  };
}

function buildSRSymbolEntries(symbol: string, levels: SRLevel[]) {
  const entries: Record<string, SRLevel[]> = {
    [symbol]: levels,
  };
  const normalized = normalizeToBase(symbol);
  if (normalized && normalized !== symbol) {
    entries[normalized] = levels;
  }
  return entries;
}


/** Per-symbol S/R settings overrides (only fields that differ from global) */
export interface SRSymbolSettings {
  maxLevels?: number;
  priceRange?: number;
  minTouchCount?: number;
  nearestLevels?: number;
}
interface SRState {
  /** S/R levels keyed by symbol */
  levelsBySymbol: Record<string, SRLevel[]>;

  /** Increments whenever fresh levels hydrate into the store */
  hydrationVersion: number;

  /** Whether S/R display is enabled globally */
  enabled: boolean;

  /** Which timeframes are visible (user toggle) */
  visibleTimeframes: Set<SRTimeframe>;

  /** Price range for clipping (±X points from current price) */
  priceRange: number;

  /** Recalculation interval in minutes */
  recalcInterval: number;

  /** Delta tolerance for clustering (e.g., 0.0007 = 0.07%) */
  deltaPercent: number;

  /** Minimum touch count to display a level (default 20) */
  minTouchCount: number;

  /** Number of levels per side (support/resistance) per timeframe */
  levelsPerTf: number;

  /** Nearest N levels per side (above + below current price). 0 = show all */
  nearestLevels: number;

  /** Display mode: 'chart' for price lines, 'table' for table view */
  displayMode: 'chart' | 'table';

  /** Line opacity for S/R levels on chart (0.0-1.0) */
  lineOpacity: number;

  /** Last update timestamp per symbol */
  lastUpdateBySymbol: Record<string, number>;

  /** Global max levels setting (from backend) */
  maxLevels: number;

  /** Per-symbol S/R settings overrides */
  perSymbolSettings: Record<string, SRSymbolSettings>;

  // Actions
  /** Set levels for a symbol (from WebSocket) */
  setLevels: (symbol: string, levels: SRLevel[]) => void;

  /** Handle S/R levels update from backend */
  handleLevelsUpdate: (update: SRLevelsUpdate) => void;

  /** Get levels for a symbol, optionally filtered by visible timeframes */
  getLevels: (symbol: string, filterVisible?: boolean) => SRLevel[];

  /** Toggle S/R display on/off */
  setEnabled: (enabled: boolean) => void;

  /** Toggle a specific timeframe visibility */
  toggleTimeframe: (tf: SRTimeframe) => void;

  /** Set which timeframes are visible */
  setVisibleTimeframes: (timeframes: SRTimeframe[]) => void;

  /** Set the price range for clipping */
  setPriceRange: (range: number) => void;

  /** Set the recalculation interval in minutes */
  setRecalcInterval: (minutes: number) => void;

  /** Set delta tolerance for clustering */
  setDeltaPercent: (delta: number) => void;

  /** Set minimum touch count filter */
  setMinTouchCount: (count: number) => void;

  /** Set levels per timeframe (per side) */
  setLevelsPerTf: (count: number) => void;

  /** Set nearest levels per side (0 = show all) */
  setNearestLevels: (count: number) => void;

  /** Set display mode (chart or table) */
  setDisplayMode: (mode: 'chart' | 'table') => void;

  /** Set line opacity (0.0-1.0) */
  setLineOpacity: (opacity: number) => void;

  /** Check if a timeframe is visible */
  isTimeframeVisible: (tf: SRTimeframe) => boolean;

  /** Clear all levels */
  clearAll: () => void;

  /** Clear levels for a specific symbol */
  clearSymbol: (symbol: string) => void;

  /** Set global max levels */
  setMaxLevels: (count: number) => void;

  /** Set a per-symbol setting override */
  setSymbolSetting: (symbol: string, key: keyof SRSymbolSettings, value: number) => void;

  /** Get effective settings for a symbol (global merged with per-symbol overrides) */
  getEffectiveSettings: (symbol: string) => { maxLevels: number; priceRange: number; minTouchCount: number; nearestLevels: number };

  /** Fetch per-symbol settings from backend */
  fetchSymbolSettings: (symbol: string) => void;

  /** Save per-symbol settings to backend */
  saveSymbolSettings: (symbol: string) => void;
}

/**
 * All available S/R timeframes (ordered by importance: highest to lowest).
 * Derived from `SR_TIMEFRAME_ORDER` so there is a single source of ordering.
 */
export const ALL_SR_TIMEFRAMES: SRTimeframe[] = [...SR_TIMEFRAME_ORDER];

export const useSRStore = create<SRState>()(
  subscribeWithSelector((set, get) => ({
  levelsBySymbol: {},
  hydrationVersion: 0,
  enabled: true,
  visibleTimeframes: new Set<SRTimeframe>(['5m', '15m', '1h', '4h', 'D', 'W']),
  priceRange: 1000, // Default ±1000 points
  recalcInterval: 240, // Default 4 hours
  deltaPercent: 0.0007, // 0.07% clustering tolerance
  minTouchCount: 0, // Default: show all levels (no minimum filter)
  levelsPerTf: 50, // Default: 50 levels per side per timeframe
  nearestLevels: 5, // Default: 5 above + 5 below current price (0 = show all)
  displayMode: 'chart', // Default: show as chart lines
  lineOpacity: 0.6, // Default: 60% opacity
  lastUpdateBySymbol: {},
  maxLevels: 200, // Default: 200 max levels per symbol
  perSymbolSettings: {},

  setLevels: (symbol, levels) => {
    const symbolEntries = buildSRSymbolEntries(symbol, levels);
    set((state) => ({
      levelsBySymbol: {
        ...state.levelsBySymbol,
        ...symbolEntries,
      },
      lastUpdateBySymbol: {
        ...state.lastUpdateBySymbol,
        [symbol]: Date.now(),
      },
      hydrationVersion: state.hydrationVersion + 1,
    }));
  },

  handleLevelsUpdate: (update) => {
    const { symbol, levels, timestamp } = update;
    const symbolEntries = buildSRSymbolEntries(symbol, levels);
    set((state) => ({
      levelsBySymbol: {
        ...state.levelsBySymbol,
        ...symbolEntries,
      },
      lastUpdateBySymbol: {
        ...state.lastUpdateBySymbol,
        [symbol]: timestamp,
      },
      hydrationVersion: state.hydrationVersion + 1,
    }));
  },

  getLevels: (symbol, filterVisible = true) => {
    const state = get();
    const levels = state.levelsBySymbol[symbol] || [];

    if (!filterVisible) {
      return levels;
    }

    // Lower timeframes (5m, 15m, 1h) are subject to minTouchCount filter
    // Higher timeframes (4h, D, W) always show regardless of touch count
    const lowerTimeframes = new Set(['5m', '15m', '1h']);
    return levels.filter((level) =>
      state.visibleTimeframes.has(level.timeframe) &&
      (!lowerTimeframes.has(level.timeframe) || level.touchCount >= state.minTouchCount)
    );
  },

  setEnabled: (enabled) => {
    set({ enabled });
  },

  toggleTimeframe: (tf) => {
    set((state) => {
      const newVisible = new Set(state.visibleTimeframes);
      if (newVisible.has(tf)) {
        // Guardrail: never allow empty visibleTimeframes
        if (newVisible.size <= 1) return state;
        newVisible.delete(tf);
      } else {
        newVisible.add(tf);
      }
      return { visibleTimeframes: newVisible };
    });
  },

  setVisibleTimeframes: (timeframes) => {
    // Guardrail: never allow empty visibleTimeframes — keep current if empty
    if (timeframes.length === 0) return;
    set({ visibleTimeframes: new Set(timeframes) });
  },

  setPriceRange: (range) => {
    set({ priceRange: range });
  },

  setRecalcInterval: (minutes) => {
    set({ recalcInterval: minutes });
  },

  setDeltaPercent: (delta) => {
    set({ deltaPercent: delta });
  },

  setMinTouchCount: (count) => {
    set({ minTouchCount: count });
  },

  setLevelsPerTf: (count) => {
    set({ levelsPerTf: count });
  },

  setNearestLevels: (count) => {
    set({ nearestLevels: count });
  },

  setDisplayMode: (mode) => {
    set({ displayMode: mode });
  },

  setLineOpacity: (opacity) => {
    set({ lineOpacity: opacity });
  },

  isTimeframeVisible: (tf) => {
    return get().visibleTimeframes.has(tf);
  },

  clearAll: () => {
    set({
      levelsBySymbol: {},
      lastUpdateBySymbol: {},
      hydrationVersion: 0,
    });
  },

  clearSymbol: (symbol) => {
    set((state) => {
      const newLevels = { ...state.levelsBySymbol };
      const newUpdates = { ...state.lastUpdateBySymbol };
      const normalized = normalizeToBase(symbol);
      delete newLevels[symbol];
      if (normalized && normalized !== symbol) {
        delete newLevels[normalized];
      }
      delete newUpdates[symbol];
      return {
        levelsBySymbol: newLevels,
        lastUpdateBySymbol: newUpdates,
      };
    });
  },

  setMaxLevels: (count) => {
    set({ maxLevels: count });
  },

  setSymbolSetting: (symbol, key, value) => {
    set((state) => {
      const existing = state.perSymbolSettings[symbol] || {};
      return {
        perSymbolSettings: {
          ...state.perSymbolSettings,
          [symbol]: { ...existing, [key]: value },
        },
      };
    });
  },

  getEffectiveSettings: (symbol) => {
    const state = get();
    const overrides = state.perSymbolSettings[symbol] || {};
    return {
      maxLevels: overrides.maxLevels ?? state.maxLevels,
      priceRange: overrides.priceRange ?? state.priceRange,
      minTouchCount: overrides.minTouchCount ?? state.minTouchCount,
      nearestLevels: overrides.nearestLevels ?? state.nearestLevels,
    };
  },

  fetchSymbolSettings: (symbol) => {
    fetch(`${getApiBaseUrl()}/sr-settings/${encodeURIComponent(symbol)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data && typeof data === 'object' && Object.keys(data).length > 0) {
          set((state) => ({
            perSymbolSettings: {
              ...state.perSymbolSettings,
              [symbol]: data as SRSymbolSettings,
            },
          }));
          console.log(`[S/R] Loaded per-symbol settings for ${symbol}:`, data);
        }
      })
      .catch((err) => {
        console.error(`[S/R] Failed to load per-symbol settings for ${symbol}:`, err);
      });
  },

  saveSymbolSettings: (symbol) => {
    const overrides = get().perSymbolSettings[symbol];
    if (!overrides) return;
    fetch(`${getApiBaseUrl()}/sr-settings/${encodeURIComponent(symbol)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(overrides),
    })
      .then((res) => {
        if (!res.ok) console.error(`[S/R] Failed to save per-symbol settings for ${symbol}`);
        else console.log(`[S/R] Saved per-symbol settings for ${symbol}`);
      })
      .catch((err) => {
        console.error(`[S/R] Failed to save per-symbol settings for ${symbol}:`, err);
      });
  },
}))
);

/** The global S/R settings as `POST /sr-settings` takes them. */
export type SRSettingsSource = Pick<SRState,
  'enabled' | 'visibleTimeframes' | 'priceRange' | 'recalcInterval' | 'deltaPercent'
  | 'minTouchCount' | 'levelsPerTf' | 'nearestLevels' | 'displayMode' | 'lineOpacity'>;

/**
 * Body of `POST /sr-settings`. The one definition: the S/R panel's auto-save
 * and `saveSRSettings` both send this, so a timeframe hidden from a chart
 * label persists exactly as one switched off in the panel.
 */
export function srSettingsPayload(s: SRSettingsSource) {
  return {
    enabled: s.enabled,
    visibleTimeframes: Array.from(s.visibleTimeframes),
    priceRange: s.priceRange,
    recalcIntervalMins: s.recalcInterval,
    deltaPercent: s.deltaPercent,
    minTouchCount: s.minTouchCount,
    supportLevelsPerTf: s.levelsPerTf,
    nearestLevels: s.nearestLevels,
    displayMode: s.displayMode,
    lineOpacity: s.lineOpacity,
  };
}

/**
 * Save the store's current global S/R settings (same request as the S/R
 * panel's auto-save) and ask for fresh levels. Resolves false on failure.
 * Saves are serialised (see below).
 */
let srSaveChain: Promise<unknown> = Promise.resolve();

export function saveSRSettings(): Promise<boolean> {
  // One at a time, body built when each is sent: a hide then Undo must land
  // on the server in that order, never as two racing POSTs.
  const next = srSaveChain.then(postSRSettings);
  srSaveChain = next;
  return next;
}

async function postSRSettings(): Promise<boolean> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/sr-settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(srSettingsPayload(useSRStore.getState())),
    });
    if (!res.ok) {
      console.error('📊 [S/R] Failed to save settings:', res.statusText);
      return false;
    }
    wsService.emit('get-sr-levels', { symbol: '' });
    return true;
  } catch (err) {
    console.error('📊 [S/R] Failed to save settings:', err);
    return false;
  }
}

// Track handler initialization to prevent duplicates
let srHandlersInitialized = false;

/**
 * Fetch fresh S/R settings from backend
 */
function fetchSRSettings(): void {
  fetch(`${getApiBaseUrl()}/sr-settings`)
    .then((res) => res.json())
    .then((data) => {
      const store = useSRStore.getState();
      if (typeof data.enabled === 'boolean') {
        store.setEnabled(data.enabled);
      }
      if (typeof data.recalcIntervalMins === 'number') {
        store.setRecalcInterval(data.recalcIntervalMins);
      }
      if (typeof data.deltaPercent === 'number') {
        store.setDeltaPercent(data.deltaPercent);
      }
      if (typeof data.priceRange === 'number') {
        store.setPriceRange(data.priceRange);
      }
      if (Array.isArray(data.visibleTimeframes)) {
        store.setVisibleTimeframes(data.visibleTimeframes as SRTimeframe[]);
      }
      if (typeof data.minTouchCount === 'number') {
        store.setMinTouchCount(data.minTouchCount);
      }
      if (typeof data.supportLevelsPerTf === 'number') {
        store.setLevelsPerTf(data.supportLevelsPerTf);
      }
      if (typeof data.nearestLevels === 'number') {
        store.setNearestLevels(data.nearestLevels);
      }
      if (typeof data.displayMode === 'string') {
        store.setDisplayMode(data.displayMode as 'chart' | 'table');
      }
      if (typeof data.lineOpacity === 'number') {
        store.setLineOpacity(data.lineOpacity);
      }
      if (typeof data.maxLevels === 'number') {
        store.setMaxLevels(data.maxLevels);
      }
      console.log(`📊 [S/R] Loaded settings: enabled=${data.enabled}, interval=${data.recalcIntervalMins}m, delta=${data.deltaPercent}, range=${data.priceRange}, minTouch=${data.minTouchCount}, levelsPerTf=${data.supportLevelsPerTf}, nearest=${data.nearestLevels}, opacity=${data.lineOpacity}`);
    })
    .catch((err) => {
      console.error('📊 [S/R] Failed to load settings:', err);
    });
}

function fetchSRLevels(): void {
  fetch(`${getApiBaseUrl()}/sr-levels`)
    .then((res) => res.json())
    .then((data) => {
      if (!data?.levels || typeof data.levels !== 'object') {
        return;
      }

      const store = useSRStore.getState();
      Object.entries(data.levels).forEach(([symbol, levels]) => {
        if (Array.isArray(levels)) {
          store.setLevels(symbol, levels as SRLevel[]);
        }
      });
      console.log(`📊 [S/R] Hydrated ${Object.keys(data.levels).length} symbols from REST fallback`);
    })
    .catch((err) => {
      console.error('📊 [S/R] Failed to hydrate levels from REST:', err);
    });
}

/**
 * Initialize S/R socket handlers
 * Call this from WebSocketService after connection
 */
export function initSRSocketHandlers(): void {
  // Clear stale levels on reconnect before hydrating fresh data
  useSRStore.getState().clearAll();

  // Always fetch fresh settings on connect/reconnect
  fetchSRSettings();
  fetchSRLevels();

  if (srHandlersInitialized) {
    console.log('📊 [S/R] Socket handlers already initialized, fetched fresh settings');
    return;
  }
  srHandlersInitialized = true;

  // Handle full levels update
  wsService.on('sr-levels', (update: SRLevelsUpdate) => {
    console.log(`📊 [S/R] Received levels for ${update.symbol}: ${update.levels.length} levels`);
    useSRStore.getState().handleLevelsUpdate(update);
  });

  // Handle single level update (optional, for real-time updates)
  wsService.on('sr-level-update', (update: SRLevelsUpdate) => {
    console.log(`📊 [S/R] Level update for ${update.symbol}`);
    useSRStore.getState().handleLevelsUpdate(update);
  });
}

/**
 * Reset S/R socket handlers
 * Call this when disconnecting
 */
export function resetSRSocketHandlers(): void {
  srHandlersInitialized = false;
  wsService.off('sr-levels');
  wsService.off('sr-level-update');
}
